/**
 * StateCard — one shape for empty, loading, error and partial-read.
 *
 * WHY THIS EXISTS. Design gate rows A1, A2, A3 and A5 are four rules about
 * states, and before this pass the product satisfied them screen by screen or
 * not at all: GAP-ANALYSIS found three hand-rolled state blocks inside nine
 * lines of routes/app/trusts/index.tsx alone, and no dashed provisional
 * treatment anywhere in 8 800 lines of stylesheet. Two of those gate rows have
 * a price attached. The dashboard showing a bare "0 trusts" cost credibility in
 * front of Destinata. Master lodgements reading 0 for twelve seconds while the
 * query settled caused three false alarms.
 *
 * So the rules are enforced by the component, not by review:
 *
 *   loading  cannot show a number. It has no value slot at all, only skeleton
 *            bars in the shape of the text that is coming, plus a plain line.
 *            A spinner on a page that already has layout is not offered.
 *   empty    REQUIRES a headline saying what would be here. The action is
 *            optional in the type only because a reader with no permission to
 *            fill it should not be shown a button they cannot use.
 *   error    REQUIRES the headline and carries a standing reassurance that the
 *            data is safe and nothing changed. That wording is deliberate for
 *            a fiduciary audience and is not the caller decision.
 *   partial  is NOT provisional: some of the page is real. Solid border, brass
 *            marker, and it says what is missing rather than rounding a partial
 *            read up to a clean one. The FICA book-health banner is the model.
 *
 * Contract: docs/redesign-20260915/design_handoff_app_cohesion/README.md,
 * "States", drawn on screen 1c under the band "The one state shape".
 *
 * Styling: styles/design-system.css, .state-card (unscoped, both shells).
 */

import type { ReactNode } from 'react';
import { Skeleton } from './ui/skeleton';

export type StateVariant = 'empty' | 'loading' | 'error' | 'partial';

export interface StateCardProps {
  variant: StateVariant;
  /**
   * The uppercase label. Defaults per variant, so a screen only overrides it to
   * name the region that failed ("ACTIVITY FEED" on a dashboard with four).
   */
  label?: string;
  /**
   * The 14px line. What would be here (empty), what failed (error), what could
   * not be read (partial), what is being read (loading).
   */
  headline: ReactNode;
  /** The 12.5px line under it. Detail, or the consequence. */
  detail?: ReactNode;
  /**
   * The one action. Omitted on loading by construction: there is nothing for a
   * reader to do while a query is in flight, and a button there invites a
   * second request for the same data.
   */
  action?: { label: string; onClick: () => void } | undefined;
  /** Test id suffix, so a screen with several regions can tell them apart. */
  testId?: string;
}

const DEFAULT_LABEL: Record<StateVariant, string> = {
  empty: 'Nothing here yet',
  loading: 'Reading',
  error: 'Could not load',
  partial: 'Partly read',
};

/** The reassurance is the component decision, not the caller decision: on a
 *  compliance product the first question after an error is whether the failure
 *  changed anything. Kept as one string so it reads identically everywhere. */
export const ERROR_REASSURANCE = 'Your data is safe and nothing was changed.';

export function StateCard({
  variant,
  label,
  headline,
  detail,
  action,
  testId,
}: StateCardProps) {
  const modifier = variant === 'empty' ? '' : ` state-card--${variant}`;

  return (
    <div
      className={`state-card${modifier}`}
      data-testid={testId ? `state-card-${testId}` : 'state-card'}
      data-variant={variant}
      /* A loading region announces itself once, on the container. Screen-reader
       * users get one polite message, not N shimmering rectangles. */
      {...(variant === 'loading' ? { 'aria-busy': true } : {})}
      {...(variant === 'error' ? { role: 'alert' } : {})}
      /* A partial read is news, not an alarm: `status` announces it politely
       * when it appears without interrupting what a reader is doing. It was
       * role="status" on the FICA book-health banner before this component
       * existed, and losing that on the way in would have been a silent
       * accessibility regression. */
      {...(variant === 'partial' ? { role: 'status' } : {})}
    >
      <span className="state-card-label">{label ?? DEFAULT_LABEL[variant]}</span>
      {variant === 'loading' ? (
        <span className="state-card-bars">
          {/* 70 / 90 / 45 per the contract. Unequal on purpose: three equal bars
              read as a progress meter rather than as text about to arrive. */}
          <Skeleton width="70%" height={13} radius={4} />
          <Skeleton width="90%" height={13} radius={4} />
          <Skeleton width="45%" height={13} radius={4} />
        </span>
      ) : null}
      <span className="state-card-headline">{headline}</span>
      {detail ? <span className="state-card-detail">{detail}</span> : null}
      {variant === 'error' ? (
        <span className="state-card-detail">{ERROR_REASSURANCE}</span>
      ) : null}
      {action && variant !== 'loading' ? (
        <button type="button" className="state-card-action" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}
